import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LegalDocumentCard from './components/LegalDocumentCard';
import LanguageSelector from './components/LanguageSelector';
import CookiePreferenceCenter from './components/CookiePreferenceCenter';
import GDPRRequestForm from './components/GDPRRequestForm';
import LegalDocumentViewer from './components/LegalDocumentViewer';
import ComplianceStatusDashboard from './components/ComplianceStatusDashboard';

const LegalHub = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeDocument, setActiveDocument] = useState(null);
  const [showCookieCenter, setShowCookieCenter] = useState(false);
  const [activeTab, setActiveTab] = useState('documents');
  const [searchQuery, setSearchQuery] = useState('');

  // Load saved language preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('rexfel_language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Save language preference
  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    localStorage.setItem('rexfel_language', language);
  };

  const legalDocuments = [
    {
      id: 'impressum',
      title: 'Legal Notice (Impressum)',
      description: 'Company information, registration details, and regulatory compliance as required by German law.',
      lastUpdated: '2024-10-01',
      documentType: 'impressum'
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      description: 'Comprehensive data protection policy detailing how we collect, use, and protect your personal information.',
      lastUpdated: '2024-09-15',
      documentType: 'privacy'
    },
    {
      id: 'cookies',
      title: 'Cookie Policy',
      description: 'Information about cookies used on our website and how to manage your cookie preferences.',
      lastUpdated: '2024-10-10',
      documentType: 'cookies'
    },
    {
      id: 'terms',
      title: 'Terms of Service',
      description: 'Terms and conditions governing the use of our investment platform and services.',
      lastUpdated: '2024-08-20',
      documentType: 'terms'
    },
    {
      id: 'gdpr',
      title: 'GDPR Information',
      description: 'Your rights under the General Data Protection Regulation and how to exercise them.',
      lastUpdated: '2024-09-30',
      documentType: 'gdpr'
    },
    {
      id: 'risk',
      title: 'Risk Disclosure',
      description: 'Important information about investment risks and regulatory disclosures for financial services.',
      lastUpdated: '2024-09-01',
      documentType: 'terms'
    }
  ];

  const tabs = [
    { id: 'documents', label: 'Legal Documents', icon: 'FileText' },
    { id: 'gdpr', label: 'GDPR Requests', icon: 'Shield' },
    { id: 'compliance', label: 'Compliance Status', icon: 'CheckCircle' },
    { id: 'cookies', label: 'Cookie Settings', icon: 'Cookie' }
  ];

  const filteredDocuments = legalDocuments?.filter(doc =>
    doc?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const getContent = () => {
    const content = {
      en: {
        title: 'Legal Hub',
        subtitle: 'Complete legal compliance center with multilingual documentation',
        searchPlaceholder: 'Search legal documents...',
        lastUpdated: 'Last updated',
        viewDocument: 'View Document',
        downloadAll: 'Download All Documents',
        contactLegal: 'Contact Legal Team'
      },
      de: {
        title: 'Rechts-Hub',
        subtitle: 'Vollständiges Rechtscompliance-Center mit mehrsprachiger Dokumentation',
        searchPlaceholder: 'Rechtsdokumente durchsuchen...',
        lastUpdated: 'Zuletzt aktualisiert',
        viewDocument: 'Dokument anzeigen',
        downloadAll: 'Alle Dokumente herunterladen',
        contactLegal: 'Rechtsabteilung kontaktieren'
      },
      fr: {
        title: 'Centre Juridique',
        subtitle: 'Centre de conformité juridique complet avec documentation multilingue',
        searchPlaceholder: 'Rechercher des documents juridiques...',
        lastUpdated: 'Dernière mise à jour',
        viewDocument: 'Voir le document',
        downloadAll: 'Télécharger tous les documents',
        contactLegal: 'Contacter l\'équipe juridique'
      },
      es: {
        title: 'Centro Legal',
        subtitle: 'Centro completo de cumplimiento legal con documentación multilingüe',
        searchPlaceholder: 'Buscar documentos legales...',
        lastUpdated: 'Última actualización',
        viewDocument: 'Ver documento',
        downloadAll: 'Descargar todos los documentos',
        contactLegal: 'Contactar equipo legal'
      }
    };
    return content?.[currentLanguage] || content?.en;
  };

  const content = getContent();

  const handleCookieSave = (preferences) => {
    localStorage.setItem('rexfel_cookie_preferences', JSON.stringify(preferences));
    setShowCookieCenter(false);
    // Here you would typically update your cookie consent management
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Legal Hub - REXFEL Investment Platform</title>
        <meta name="description" content="Complete legal compliance center with GDPR documentation, privacy policies, and multilingual legal framework for REXFEL Investment Platform." />
        <meta name="keywords" content="legal, GDPR, privacy policy, compliance, impressum, cookies, data protection" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-brand-sm text-sm font-medium mb-4">
                <Icon name="Shield" size={16} />
                <span>Legal Compliance Center</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                {content?.title}
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                {content?.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="default"
                  className="bg-gradient-to-r from-primary to-accent"
                  onClick={() => setActiveTab('gdpr')}
                >
                  <Icon name="FileText" size={16} />
                  <span className="ml-2">Submit GDPR Request</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowCookieCenter(true)}
                >
                  <Icon name="Cookie" size={16} />
                  <span className="ml-2">Manage Cookies</span>
                </Button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-card border border-border rounded-brand p-4 text-center">
                <div className="text-2xl font-bold text-primary mb-1">{legalDocuments?.length}</div>
                <div className="text-sm text-muted-foreground">Legal Documents</div>
              </div>
              <div className="bg-card border border-border rounded-brand p-4 text-center">
                <div className="text-2xl font-bold text-success mb-1">4</div>
                <div className="text-sm text-muted-foreground">Languages</div>
              </div>
              <div className="bg-card border border-border rounded-brand p-4 text-center">
                <div className="text-2xl font-bold text-accent mb-1">100%</div>
                <div className="text-sm text-muted-foreground">GDPR Compliant</div>
              </div>
              <div className="bg-card border border-border rounded-brand p-4 text-center">
                <div className="text-2xl font-bold text-warning mb-1">24/7</div>
                <div className="text-sm text-muted-foreground">Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-6">
                <LanguageSelector
                  currentLanguage={currentLanguage}
                  onLanguageChange={handleLanguageChange}
                  availableLanguages={['en', 'de', 'fr', 'es']}
                />

                {/* Navigation Tabs */}
                <div className="bg-card border border-border rounded-brand p-4">
                  <h3 className="font-semibold text-foreground mb-4">Navigation</h3>
                  <nav className="space-y-2">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-brand-sm transition-brand text-left ${
                          activeTab === tab?.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span className="font-medium">{tab?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Contact Legal Team */}
                <div className="bg-card border border-border rounded-brand p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="MessageCircle" size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Need Help?</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Contact our legal team for assistance with compliance questions.
                  </p>
                  <Button variant="outline" size="sm" fullWidth>
                    <Icon name="Mail" size={14} />
                    <span className="ml-2">{content?.contactLegal}</span>
                  </Button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="lg:col-span-3">
                {activeTab === 'documents' && (
                  <div className="space-y-6">
                    {/* Search and Actions */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 relative">
                        <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder={content?.searchPlaceholder}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                          className="w-full pl-10 pr-4 py-2 border border-border rounded-brand bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        />
                      </div>
                      <Button variant="outline">
                        <Icon name="Download" size={16} />
                        <span className="ml-2">{content?.downloadAll}</span>
                      </Button>
                    </div>

                    {/* Document Viewer or Grid */}
                    {activeDocument ? (
                      <LegalDocumentViewer
                        document={activeDocument}
                        currentLanguage={currentLanguage}
                        onClose={() => setActiveDocument(null)}
                      />
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredDocuments?.map((document) => (
                          <LegalDocumentCard
                            key={document?.id}
                            title={document?.title}
                            description={document?.description}
                            lastUpdated={document?.lastUpdated}
                            documentType={document?.documentType}
                            icon="FileText"
                            onClick={() => setActiveDocument(document?.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'gdpr' && (
                  <GDPRRequestForm currentLanguage={currentLanguage} />
                )}

                {activeTab === 'compliance' && (
                  <ComplianceStatusDashboard currentLanguage={currentLanguage} />
                )}

                {activeTab === 'cookies' && (
                  <div className="bg-card border border-border rounded-brand p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="p-3 bg-primary/10 rounded-brand-sm">
                        <Icon name="Cookie" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">Cookie Management</h2>
                        <p className="text-sm text-muted-foreground">Manage your cookie preferences and privacy settings</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-muted/50 rounded-brand p-4">
                        <h3 className="font-medium text-foreground mb-2">Current Cookie Status</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-success rounded-full"></div>
                            <span className="text-muted-foreground">Necessary: Active</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                            <span className="text-muted-foreground">Analytics: Disabled</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                            <span className="text-muted-foreground">Marketing: Disabled</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-success rounded-full"></div>
                            <span className="text-muted-foreground">Functional: Active</span>
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="default"
                        onClick={() => setShowCookieCenter(true)}
                        className="bg-gradient-to-r from-primary to-accent"
                      >
                        <Icon name="Settings" size={16} />
                        <span className="ml-2">Open Cookie Preference Center</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Trust & Compliance</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                REXFEL maintains the highest standards of legal compliance and data protection across all jurisdictions.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-brand mx-auto mb-4 flex items-center justify-center">
                  <Icon name="Shield" size={32} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">GDPR Compliant</h3>
                <p className="text-sm text-muted-foreground">Full compliance with EU data protection regulations</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-brand mx-auto mb-4 flex items-center justify-center">
                  <Icon name="Building2" size={32} className="text-success" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">BaFin Regulated</h3>
                <p className="text-sm text-muted-foreground">Licensed by German financial authority</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-brand mx-auto mb-4 flex items-center justify-center">
                  <Icon name="Lock" size={32} className="text-accent" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">ISO 27001</h3>
                <p className="text-sm text-muted-foreground">Information security management certified</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-warning/10 rounded-brand mx-auto mb-4 flex items-center justify-center">
                  <Icon name="Award" size={32} className="text-warning" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Hamburg CoC</h3>
                <p className="text-sm text-muted-foreground">Chamber of Commerce member</p>
              </div>
            </div>
          </div>
        </section>
      </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center">
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-foreground mb-2">REXFEL Legal Hub</h3>
                <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
                  This legal hub provides access to important legal documents and compliance information. 
                  All documents are subject to periodic updates. Please check regularly for the latest versions.
                </p>
              </div>

              <div className="border-t border-border pt-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    © {new Date()?.getFullYear()} REXFEL Technology and Innovation. All rights reserved.
                  </p>
                  <div className="flex items-center space-x-6 mt-4 md:mt-0">
                    <span className="text-xs text-muted-foreground">Hamburg Commercial Register</span>
                    <span className="text-xs text-muted-foreground">EU Regulated Entity</span>
                    <span className="text-xs text-muted-foreground">GDPR Compliant</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>

      {/* Cookie Preference Center Modal */}
      <CookiePreferenceCenter
        isOpen={showCookieCenter}
        onClose={() => setShowCookieCenter(false)}
        onSave={handleCookieSave}
      />
    </div>
  );
};

export default LegalHub;